import React, { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import EntityTasksTab from "../tasks/EntityTasksTab";
import EntityWorkReportsTab from "../tasks/EntityWorkReportsTab";
import EntityActivityFeed from "../activity/EntityActivityFeed";
import TenancyCancelDialog from "./TenancyCancelDialog";
import {
  coerceRevenueTypeForFrequency,
  monthlyEquivalentFromRevenueRows,
  normalizeRevenueFrequency,
  oneTimeBreakdownEntries,
  recurringMonthlyBreakdownEntries,
  revenueFrequencyLabel,
  revenueTypeLabelForDisplay,
  totalOneTimeRevenueFromRows,
} from "../../../utils/tenancyRevenueBreakdown";
import {
  applyRecurringRevenueDatesFromTenancy,
  dateOnlyOrNull,
  deriveTenancyLifecyclePreviewForAssign,
  formatChfRent,
  formatMinimumDurationMonthsDe,
  formatNoticePeriodMonthsDe,
  revenueRowZeitraumDisplay,
  tenancyDateRangeLabel,
  tenancyDisplayEndIso,
  tenancyDisplayStatusLabelDe,
  tenancyDraftDisplayEndIso,
  tenancyUnitLabel,
} from "./tenantDetailHelpers";
import { formatDateOnly } from "../../../utils/datetime";
import TenancyDetailCard, {
  TenancySummaryRow,
} from "./tenancies/TenancyDetailCard";
import {
  inputClass,
  labelClass,
  tableClass,
  tdCellClass,
  TENANCY_LIFECYCLE_PREVIEW_BADGE_CLASS,
  TENANCY_LIFECYCLE_STATUS_BADGE_CLASS,
  thCellClass,
} from "./tenantDetailStyles";
import RevenueTypeSelect from "./RevenueTypeSelect";

export default function TenancyCard({
  tn,
  tenantId,
  tenant,
  expanded = false,
  onToggleExpand,
  activityRefreshKey,
  // M1c-E: passed straight through to the detail card, which owns the document
  // list this signal is about.
  documentsRefreshSignal,
  tenancyEditingId,
  tenancyEditMoveIn,
  setTenancyEditMoveIn,
  tenancyEditMoveOut,
  setTenancyEditMoveOut,
  tenancyEditCancellationOpen,
  setTenancyEditCancellationOpen,
  // KND-2: the head-lease cap now travels to the termination block, which
  // owns the termination date; this form no longer edits it.
  terminationMaxIso,
  tenancyEditActualMoveOut,
  setTenancyEditActualMoveOut,
  tenancyEditMinimumDurationMonths,
  setTenancyEditMinimumDurationMonths,
  tenancyEditNoticePeriodMonths,
  setTenancyEditNoticePeriodMonths,
  tenancyEditSaving,
  tenancyEditSectionRef,
  revenueForm,
  setRevenueForm,
  revenueEditingId,
  tenancyRevenueByTenancyId,
  tenancyRevenueLoadingId,
  tenancyRevenueErr,
  inspectionsList,
  inspectionDetailById,
  openOrCreateInspection,
  startTenancyEdit,
  cancelTenancyEdit,
  submitTenancyEdit,
  submitRevenueForm,
  startRevenueEdit,
  cancelRevenueEdit,
  deleteRevenueRow,
  navigate,
  // K4d: pass-through only. The deposit's editors are opened by the page, for
  // the row this card already has; nothing about a deposit is derived here.
  onEditDeposit,
  onEditDepositPayout,
  onTenancyCancelled,
  onParticipantsChanged,
  // KND-2: pass-through to the termination block (page reload + Verlauf).
  onTerminationChanged,
}) {
  // Slice 3: the card owns only WHICH tenancy is being cancelled. The reason,
  // the no-show confirmation, the preflight and the submit all live inside
  // TenancyCancelDialog — the dialog is the one place that knows whether a
  // cancellation may proceed, and it asks the server rather than this card.
  const [cancelTarget, setCancelTarget] = useState(null);
  // Move-in confirm dialog: only shown when the edited move_in_date actually
  // differs from the stored value (same normalized-ISO comparison the parent uses
  // for the dirty-checked PATCH body). An emptied field is treated as "no change".
  const [moveInConfirmOpen, setMoveInConfirmOpen] = useState(false);
  const moveInNewNorm = dateOnlyOrNull(tenancyEditMoveIn);
  const moveInOldNorm = dateOnlyOrNull(tn.move_in_date);
  const moveInChanged = Boolean(moveInNewNorm) && moveInNewNorm !== moveInOldNorm;
  const handleTenancyEditSaveClick = () => {
    if (moveInChanged) {
      setMoveInConfirmOpen(true);
      return;
    }
    submitTenancyEdit();
  };
  const handleMoveInConfirm = () => {
    setMoveInConfirmOpen(false);
    submitTenancyEdit();
  };
  /**
   * Document count for the KPI tile.
   *
   * Previously TenancyCard fetched documents AND damage claims on mount purely
   * to fill two counters, for every tenancy on the page, whether or not the
   * reader ever opened this tab. `TenancyDocumentsSection` already reports its
   * own count through `onCountChange`, and it now renders inside the expanded
   * card — so the separate fetch pair is gone and a collapsed tenancy costs
   * nothing. `null` renders an em dash, exactly as before.
   */
  const [documentCount, setDocumentCount] = useState(null);

  const handleCancelled = () => {
    setCancelTarget(null);
    if (typeof cancelTenancyEdit === "function") cancelTenancyEdit();
    if (typeof onTenancyCancelled === "function") onTenancyCancelled();
  };

  const rowKey = tn.id != null ? String(tn.id) : `${tn.move_in_date}-${tn.room_id}`;
  const isEditing = String(tenancyEditingId) === String(tn.id);
  // "Keine Miete" detection: prefer the backend has_rent_revenue flag; only
  // fall back to client-loaded revenue rows when the flag is absent (legacy
  // payload). Render nothing while neither source is available.
  const _backendHasRentFlag =
    typeof tn.has_rent_revenue === "boolean" ? tn.has_rent_revenue : null;
  const _loadedRevRowsForRentCheck =
    tenancyRevenueByTenancyId && tn.id != null
      ? tenancyRevenueByTenancyId[String(tn.id)]
      : undefined;
  const _loadedHasRent = Array.isArray(_loadedRevRowsForRentCheck)
    ? _loadedRevRowsForRentCheck.some(
        (r) => String(r?.type || "").toLowerCase() === "rent"
      )
    : null;
  const showNoRentRevenueWarning =
    _backendHasRentFlag === false ||
    (_backendHasRentFlag === null && _loadedHasRent === false);
  /**
   * Read-only revenue list for the Finanzen disclosure.
   *
   * The rows are already on the page (`tenancyRevenueByTenancyId`), so this
   * costs no request. It is a summary only — creating, editing and deleting a
   * position still happens in the edit form, which is where those handlers
   * live. Previously this block AND the edit form's copy of it were on screen
   * at the same time, because read-only and edit were siblings.
   */
  const renderRevenueSummary = () => {
    const rows = tn.id != null ? tenancyRevenueByTenancyId[String(tn.id)] : undefined;
    if (rows === undefined) {
      return <p className="m-0 text-[12.5px] text-muted-foreground">Wird geladen …</p>;
    }
    if (!Array.isArray(rows) || rows.length === 0) {
      return (
        <p className="m-0 text-[12.5px] text-muted-foreground">
          Keine Einnahmen erfasst.
        </p>
      );
    }
    return (
      <ul className="m-0 list-none divide-y divide-border p-0">
        {rows.map((r) => (
          <li
            key={String(r.id)}
            className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-[12.5px]"
          >
            <span className="text-foreground">{r.type}</span>
            <span className="text-muted-foreground">{r.frequency}</span>
            <span className="ml-auto tabular-nums text-foreground">
              {formatChfRent(r.amount_chf)}
            </span>
          </li>
        ))}
      </ul>
    );
  };

  /**
   * The tenancy's own work streams. Unchanged components, restacked: there is
   * no standalone tenancy route, so this card is still their only home.
   */
  const renderTenancyWorkStreams = () => (
    <div className="flex flex-col gap-6">
      <EntityTasksTab bare filter={{ tenancy_id: tn.id }} title="Aufgaben" />
      <EntityWorkReportsTab
        scope="tenancy"
        entityId={tn.id}
        headingIdSuffix={tn.id}
      />
      <EntityActivityFeed
        entityType="tenancy"
        entityId={tn.id}
        headingId={`tenancy-activity-heading-${tn.id}`}
        // Copies the pattern AdminTaskDetailPage / AdminWorkReportDetailPage
        // already use: the feed's load effect depends on refreshKey, so a
        // sibling mutation no longer leaves an open feed showing stale rows.
        refreshKey={activityRefreshKey}
      />
    </div>
  );

  return (
                            <React.Fragment>
                              {isEditing ? null : expanded ? (
                                <div className="rounded-[12px] border border-border bg-card p-4 sm:p-5">
                                  {showNoRentRevenueWarning ? (
                                    <div className="mb-4 rounded-[8px] border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-[12.5px] text-amber-700 dark:text-amber-400">
                                      Für dieses Mietverhältnis ist keine Miet-Einnahme erfasst.
                                    </div>
                                  ) : null}
                                  <TenancyDetailCard
                                    tn={tn}
                                    documentsRefreshSignal={documentsRefreshSignal}
                                    tenant={tenant}
                                    viewerTenantId={tenantId}
                                    documentCount={documentCount}
                                    onDocumentCountChange={setDocumentCount}
                                    onEdit={() => startTenancyEdit(tn)}
                                    onOpenUnit={() => navigate(`/admin/units/${tn.unit_id}`)}
                                    onEditDeposit={onEditDeposit}
                                    onEditDepositPayout={onEditDepositPayout}
                                    editDisabled={tenancyEditSaving}
                                    inspectionsList={inspectionsList}
                                    inspectionDetailById={inspectionDetailById}
                                    openOrCreateInspection={openOrCreateInspection}
                                    onParticipantsChanged={onParticipantsChanged}
                                    terminationMaxIso={terminationMaxIso}
                                    onTerminationChanged={onTerminationChanged}
                                    revenueSummary={renderRevenueSummary()}
                                    activityFeed={renderTenancyWorkStreams()}
                                  />
                                </div>
                              ) : (
                                <TenancySummaryRow
                                  tn={tn}
                                  viewerTenantId={tenantId}
                                  expanded={expanded}
                                  onToggle={onToggleExpand}
                                />
                              )}
                              {String(tenancyEditingId) === String(tn.id) ? (
                                <div
                                  ref={tenancyEditSectionRef}
                                  className="mt-4 scroll-mt-6 rounded-[14px] border border-black/10 bg-slate-100 p-5 dark:border-white/[0.07] dark:bg-[#111520]"
                                >
                                    <div className="mb-2 text-[12px] font-bold text-[#0f172a] dark:text-[#eef2ff]">
                                      Mietverhältnis bearbeiten
                                    </div>
                                    <div
                                      className="mb-2.5 w-full text-[11px] leading-snug text-[#7f8daa]"
                                      style={{ lineHeight: 1.45 }}
                                    >
                                      <div className="font-bold text-[#0f172a] dark:text-[#eef2ff]">
                                        Mietende (automatisch berechnet)
                                      </div>
                                      <div className="mt-0.5 mb-1 text-[10px] text-[#7f8daa]">
                                        ergibt sich aus Kündigung &amp; Rückgabe
                                      </div>
                                      <strong className="text-[13px] font-extrabold text-[#0f172a] dark:text-[#eef2ff]">
                                        {formatDateOnly(
                                          tenancyDraftDisplayEndIso(
                                            tenancyEditActualMoveOut,
                                            tn.termination_effective_date
                                          ) || tenancyDisplayEndIso(tn)
                                        )}
                                      </strong>
                                    </div>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "flex-end" }}>
                                      <div>
                                        <label htmlFor={`ten-mi-${rowKey}`} className={labelClass}>
                                          Mietbeginn / Einzug
                                        </label>
                                        <input
                                          id={`ten-mi-${rowKey}`}
                                          type="date"
                                          className={inputClass}
                                          value={tenancyEditMoveIn}
                                          onChange={(e) => setTenancyEditMoveIn(e.target.value)}
                                          disabled={tenancyEditSaving}
                                        />
                                        <div className="mt-0.5 text-[10px] text-[#7f8daa]">
                                          Erster Miet- und Belegungstag.
                                        </div>
                                      </div>
                                      <div>
                                        <label htmlFor={`ten-mo-${rowKey}`} className={labelClass}>
                                          Befristet bis / Vertragsende
                                        </label>
                                        <input
                                          id={`ten-mo-${rowKey}`}
                                          type="date"
                                          className={inputClass}
                                          value={tenancyEditMoveOut}
                                          onChange={(e) => setTenancyEditMoveOut(e.target.value)}
                                          disabled={tenancyEditSaving}
                                        />
                                        <div className="mt-0.5 text-[10px] text-[#7f8daa]">
                                          Vertragliches Enddatum. Keine Kündigung.
                                        </div>
                                      </div>
                                      <div style={{ flexBasis: "100%" }}>
                                        <button
                                          type="button"
                                          onClick={() => setTenancyEditCancellationOpen((v) => !v)}
                                          disabled={tenancyEditSaving}
                                          className="rounded-[8px] border border-black/10 bg-transparent px-3 py-2 text-[12px] font-semibold text-[#64748b] hover:bg-slate-100 dark:border-white/[0.1] dark:text-[#8090b0] dark:hover:bg-white/[0.04]"
                                          style={{ cursor: tenancyEditSaving ? "default" : "pointer" }}
                                        >
                                          {tenancyEditCancellationOpen
                                            ? "Rückgabe ausblenden"
                                            : "Rückgabe erfassen"}
                                        </button>
                                        <div className="mt-1 text-[10px] text-muted-foreground">
                                          Die Kündigung wird im Bereich „Kündigung“ der Mietvertrag-Karte erfasst.
                                        </div>
                                      </div>
                                      {tenancyEditCancellationOpen ? (
                                      <div>
                                        <label htmlFor={`ten-am-${rowKey}`} className={labelClass}>
                                          Rückgabe erfolgt am
                                        </label>
                                        <input
                                          id={`ten-am-${rowKey}`}
                                          type="date"
                                          className={inputClass}
                                          value={tenancyEditActualMoveOut}
                                          onChange={(e) => setTenancyEditActualMoveOut(e.target.value)}
                                          disabled={tenancyEditSaving}
                                        />
                                      </div>
                                      ) : null}
                                      <div>
                                        <label htmlFor={`ten-st-${rowKey}`} className={labelClass}>
                                          Status (abgeleitet)
                                        </label>
                                        <div className={`${inputClass} flex items-center`}>
                                          {(() => {
                                            const key = deriveTenancyLifecyclePreviewForAssign(
                                              tn.move_in_date,
                                              tn.termination_effective_date,
                                              tenancyEditActualMoveOut
                                            );
                                            const badgeClass =
                                              TENANCY_LIFECYCLE_PREVIEW_BADGE_CLASS[key] ||
                                              TENANCY_LIFECYCLE_PREVIEW_BADGE_CLASS.active;
                                            return (
                                              <span className={badgeClass}>
                                                {tenancyDisplayStatusLabelDe(key)}
                                              </span>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                      <div>
                                        <label htmlFor={`ten-mdm-${rowKey}`} className={labelClass}>
                                          Mindestmietdauer
                                        </label>
                                        <select
                                          id={`ten-mdm-${rowKey}`}
                                          className={inputClass}
                                          style={{ cursor: tenancyEditSaving ? "default" : "pointer" }}
                                          value={tenancyEditMinimumDurationMonths}
                                          onChange={(e) =>
                                            setTenancyEditMinimumDurationMonths(e.target.value)
                                          }
                                          disabled={tenancyEditSaving}
                                        >
                                          <option value="0">Keine Mindestdauer</option>
                                          <option value="1">1 Monat</option>
                                          <option value="2">2 Monate</option>
                                          <option value="3">3 Monate</option>
                                          <option value="6">6 Monate</option>
                                          <option value="12">12 Monate</option>
                                        </select>
                                      </div>
                                      <div>
                                        <label htmlFor={`ten-npm-${rowKey}`} className={labelClass}>
                                          Kündigungsfrist
                                        </label>
                                        <select
                                          id={`ten-npm-${rowKey}`}
                                          className={inputClass}
                                          style={{ cursor: tenancyEditSaving ? "default" : "pointer" }}
                                          value={tenancyEditNoticePeriodMonths}
                                          onChange={(e) =>
                                            setTenancyEditNoticePeriodMonths(e.target.value)
                                          }
                                          disabled={tenancyEditSaving}
                                        >
                                          <option value="0">Sofortige Kündigung</option>
                                          <option value="1">1 Monat</option>
                                          <option value="2">2 Monate</option>
                                          <option value="3">3 Monate</option>
                                        </select>
                                      </div>
                                      <div style={{ display: "flex", gap: "8px" }}>
                                        <button
                                          type="button"
                                          onClick={handleTenancyEditSaveClick}
                                          disabled={tenancyEditSaving}
                                          className={`rounded-[8px] bg-gradient-to-r from-[#5b8cff] to-[#7c5cfc] px-3 py-1.5 text-xs font-semibold text-white ${
                                            tenancyEditSaving ? "cursor-default opacity-50" : "cursor-pointer"
                                          }`}
                                          style={{ border: "none" }}
                                        >
                                          {tenancyEditSaving ? "Speichern …" : "Speichern"}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={cancelTenancyEdit}
                                          disabled={tenancyEditSaving}
                                          className="rounded-[8px] border border-black/10 bg-transparent px-3 py-1.5 text-xs font-semibold text-[#64748b] hover:bg-slate-100 dark:border-white/[0.1] dark:text-[#8090b0] dark:hover:bg-white/[0.04] disabled:cursor-default"
                                          style={{ cursor: tenancyEditSaving ? "default" : "pointer" }}
                                        >
                                          Abbrechen
                                        </button>
                                        {tn.can_cancel === true ? (
                                          <button
                                            type="button"
                                            onClick={() => setCancelTarget(tn)}
                                            disabled={tenancyEditSaving}
                                            className="rounded-[8px] border border-rose-500/30 bg-transparent px-3 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-500/10 dark:text-rose-300 disabled:opacity-60 disabled:cursor-not-allowed"
                                          >
                                            Mietverhältnis stornieren
                                          </button>
                                        ) : null}
                                      </div>
                                    </div>

                                    <div className="mb-2 border-t border-black/10 pt-3 dark:border-white/[0.05]">
                                      <div className="mb-2 text-[9px] font-bold uppercase tracking-[1px] text-[#64748b] dark:text-[#6b7a9a]">
                                        Einnahmen
                                      </div>

                                      {(() => {
                                        const tidKpi = String(tn.id);
                                        const rowsKpi = tenancyRevenueByTenancyId[tidKpi];
                                        const kpiLoading = tenancyRevenueLoadingId === tidKpi;
                                        if (rowsKpi === undefined || kpiLoading) {
                                          return (
                                            <div className="mb-2.5 rounded-lg border border-black/10 bg-slate-100 px-3 py-2.5 text-xs text-[#64748b] dark:border-white/[0.05] dark:bg-[#111520] dark:text-[#6b7a9a]">
                                              …
                                            </div>
                                          );
                                        }
                                        const monthlyKpi = monthlyEquivalentFromRevenueRows(rowsKpi);
                                        const oneTimeKpi = totalOneTimeRevenueFromRows(rowsKpi);
                                        const recBr = recurringMonthlyBreakdownEntries(rowsKpi);
                                        const otBr = oneTimeBreakdownEntries(rowsKpi);
                                        return (
                                          <div className="mb-2.5 rounded-lg border border-black/10 bg-slate-100 px-3 py-2.5 text-xs text-[#0f172a] dark:border-white/[0.05] dark:bg-[#111520] dark:text-[#eef2ff]">
                                            <div className="mb-2 text-[9px] font-extrabold uppercase tracking-[1px] text-[#64748b] dark:text-[#6b7a9a]">
                                              Einnahmen-Übersicht
                                            </div>
                                            <div className="mb-1">
                                              <span className="font-semibold text-[#64748b] dark:text-[#6b7a9a]">Gesamteinnahmen / Monat:</span>{" "}
                                              <span className="font-bold text-[#0f172a] dark:text-[#eef2ff]">{formatChfRent(monthlyKpi)}</span>
                                            </div>
                                            <div className={recBr.length || otBr.length ? "mb-2" : ""}>
                                              <span className="font-semibold text-[#64748b] dark:text-[#6b7a9a]">Einmalige Einnahmen:</span>{" "}
                                              <span className="font-bold text-[#0f172a] dark:text-[#eef2ff]">{formatChfRent(oneTimeKpi)}</span>
                                            </div>
                                            {recBr.length || otBr.length ? (
                                              <div className="mt-2 border-t border-black/10 pt-2 dark:border-white/[0.05]">
                                                {recBr.length ? (
                                                  <div className={otBr.length ? "mb-2" : ""}>
                                                    <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.5px] text-[#64748b] dark:text-[#6b7a9a]">
                                                      Einnahmen Zusammensetzung
                                                    </div>
                                                    {recBr.map((b) => (
                                                      <div
                                                        key={b.typeKey}
                                                        className="flex justify-between gap-3 text-xs leading-snug text-[#0f172a] dark:text-[#eef2ff]"
                                                      >
                                                        <span className="text-[#64748b] dark:text-[#7f8daa]">{b.label}</span>
                                                        <span className="whitespace-nowrap font-semibold text-[#0f172a] dark:text-[#eef2ff]">
                                                          {formatChfRent(b.total)}
                                                        </span>
                                                      </div>
                                                    ))}
                                                  </div>
                                                ) : null}
                                                {otBr.length ? (
                                                  <div>
                                                    <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.5px] text-[#64748b] dark:text-[#6b7a9a]">
                                                      Einmalige Einnahmen
                                                    </div>
                                                    {otBr.map((b) => (
                                                      <div
                                                        key={b.typeKey}
                                                        className="flex justify-between gap-3 text-xs leading-snug text-[#0f172a] dark:text-[#eef2ff]"
                                                      >
                                                        <span className="text-[#64748b] dark:text-[#7f8daa]">{b.label}</span>
                                                        <span className="whitespace-nowrap font-semibold text-[#0f172a] dark:text-[#eef2ff]">
                                                          {formatChfRent(b.total)}
                                                        </span>
                                                      </div>
                                                    ))}
                                                  </div>
                                                ) : null}
                                              </div>
                                            ) : null}
                                          </div>
                                        );
                                      })()}

                                      {tenancyRevenueErr ? (
                                        <p style={{ margin: "0 0 10px 0", fontSize: "13px", color: "#f87171" }}>
                                          {tenancyRevenueErr}
                                        </p>
                                      ) : null}

                                      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "flex-end" }}>
                                        <div>
                                          <label htmlFor={`rev-type-${String(tn.id)}`} className={labelClass}>
                                            Typ
                                          </label>
                                          <RevenueTypeSelect
                                            id={`rev-type-${String(tn.id)}`}
                                            selectClassName={inputClass}
                                            value={revenueForm.type}
                                            frequency={revenueForm.frequency}
                                            onChange={(e) => setRevenueForm((f) => ({ ...f, type: e.target.value }))}
                                            disabled={tenancyRevenueLoadingId === String(tn.id)}
                                          />
                                        </div>
                                        <div>
                                          <label className={labelClass}>Betrag (CHF)</label>
                                          <input
                                            type="text"
                                            inputMode="decimal"
                                            className={inputClass}
                                            value={revenueForm.amount_chf}
                                            onChange={(e) => setRevenueForm((f) => ({ ...f, amount_chf: e.target.value }))}
                                            disabled={tenancyRevenueLoadingId === String(tn.id)}
                                            placeholder="z. B. 1200"
                                          />
                                        </div>
                                        <div>
                                          <label className={labelClass}>Frequenz</label>
                                          <select
                                            className={inputClass}
                                            style={{ cursor: tenancyRevenueLoadingId === String(tn.id) ? "default" : "pointer" }}
                                            value={revenueForm.frequency}
                                            onChange={(e) => {
                                              const nextFreq = e.target.value;
                                              setRevenueForm((f) => {
                                                const updated = applyRecurringRevenueDatesFromTenancy(
                                                  f,
                                                  nextFreq,
                                                  tn.move_in_date,
                                                  tenancyDraftDisplayEndIso(
                                                    tenancyEditActualMoveOut,
                                                    tn.termination_effective_date
                                                  ) || tenancyDisplayEndIso(tn) ||
                                                    ""
                                                );
                                                // Setup-Fee intake safety: switching to
                                                // one_time coerces the type to the only
                                                // allowed value so a stale legacy type
                                                // (cleaning/furniture/other) can never be
                                                // submitted.
                                                return {
                                                  ...updated,
                                                  type: coerceRevenueTypeForFrequency(updated.type, nextFreq),
                                                };
                                              });
                                            }}
                                            disabled={tenancyRevenueLoadingId === String(tn.id)}
                                          >
                                            <option value="monthly">Monatlich</option>
                                            <option value="yearly">Jährlich</option>
                                            <option value="one_time">Einmalig</option>
                                          </select>
                                        </div>
                                        <div>
                                          <label className={labelClass}>Start (optional)</label>
                                          <input
                                            type="date"
                                            className={inputClass}
                                            value={revenueForm.start_date}
                                            onChange={(e) => setRevenueForm((f) => ({ ...f, start_date: e.target.value }))}
                                            disabled={tenancyRevenueLoadingId === String(tn.id)}
                                          />
                                        </div>
                                        {normalizeRevenueFrequency(revenueForm.frequency) === "one_time" ? (
                                          <div>
                                            <label className={labelClass}>Ende (optional)</label>
                                            <input
                                              type="date"
                                              className={inputClass}
                                              value={revenueForm.end_date}
                                              onChange={(e) => setRevenueForm((f) => ({ ...f, end_date: e.target.value }))}
                                              disabled={tenancyRevenueLoadingId === String(tn.id)}
                                            />
                                          </div>
                                        ) : (
                                          <div>
                                            <label className={labelClass}>Ende (Mietende)</label>
                                            <div className={`${inputClass} font-semibold`}>
                                              {formatDateOnly(
                                                tenancyDraftDisplayEndIso(
                                                  tenancyEditActualMoveOut,
                                                  tn.termination_effective_date
                                                ) || tenancyDisplayEndIso(tn) ||
                                                  ""
                                              )}
                                            </div>
                                            <div className="mt-1 max-w-[220px] text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                                              Ende wird automatisch aus Mietende übernommen.
                                            </div>
                                          </div>
                                        )}
                                        <div style={{ minWidth: "240px", flex: "1 1 240px" }}>
                                          <label className={labelClass}>Notizen (optional)</label>
                                          <input
                                            type="text"
                                            className={inputClass}
                                            value={revenueForm.notes}
                                            onChange={(e) => setRevenueForm((f) => ({ ...f, notes: e.target.value }))}
                                            disabled={tenancyRevenueLoadingId === String(tn.id)}
                                            placeholder="z. B. Möbelpauschale"
                                          />
                                        </div>
                                        <div style={{ display: "inline-flex", gap: "8px" }}>
                                          <button
                                            type="button"
                                            onClick={() => submitRevenueForm(tn.id, tn)}
                                            disabled={tenancyRevenueLoadingId === String(tn.id)}
                                            className={`rounded-[8px] bg-gradient-to-r from-[#5b8cff] to-[#7c5cfc] px-3 py-1.5 text-xs font-semibold text-white ${
                                              tenancyRevenueLoadingId === String(tn.id) ? "cursor-default opacity-50" : "cursor-pointer"
                                            }`}
                                            style={{ border: "none", fontWeight: 800 }}
                                          >
                                            {revenueEditingId ? "Aktualisieren" : "Hinzufügen"}
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => cancelRevenueEdit(tn)}
                                            disabled={tenancyRevenueLoadingId === String(tn.id)}
                                            className="rounded-[8px] border border-black/10 bg-transparent px-3 py-1.5 text-xs font-semibold text-[#64748b] hover:bg-slate-100 dark:border-white/[0.1] dark:text-[#8090b0] dark:hover:bg-white/[0.04] disabled:cursor-default"
                                            style={{ cursor: tenancyRevenueLoadingId === String(tn.id) ? "default" : "pointer", fontWeight: 700 }}
                                          >
                                            Abbrechen
                                          </button>
                                        </div>
                                      </div>

                                      <div style={{ marginTop: "10px", overflowX: "auto" }}>
                                        <table className={tableClass}>
                                          <thead>
                                            <tr>
                                              <th className={thCellClass}>Typ</th>
                                              <th className={`${thCellClass} text-right`}>Betrag</th>
                                              <th className={thCellClass}>Frequenz</th>
                                              <th className={thCellClass}>Zeitraum (optional)</th>
                                              <th className={thCellClass}>Notizen</th>
                                              <th className={`${thCellClass} whitespace-nowrap text-right`}>Aktion</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {Array.isArray(tenancyRevenueByTenancyId?.[String(tn.id)]) &&
                                            tenancyRevenueByTenancyId[String(tn.id)].length > 0 ? (
                                              tenancyRevenueByTenancyId[String(tn.id)].map((rr) => {
                                                const rid = String(rr.id);
                                                const range = revenueRowZeitraumDisplay(rr, tn);
                                                return (
                                                  <tr key={rid}>
                                                    <td className={tdCellClass}>{revenueTypeLabelForDisplay(rr.type)}</td>
                                                    <td className={`${tdCellClass} text-right font-bold text-[#0f172a] dark:text-[#eef2ff]`}>
                                                      {formatChfRent(rr.amount_chf)}
                                                    </td>
                                                    <td className={tdCellClass}>{revenueFrequencyLabel(rr.frequency)}</td>
                                                    <td className={tdCellClass}>{range}</td>
                                                    <td className={tdCellClass}>{rr.notes || "—"}</td>
                                                    <td className={`${tdCellClass} whitespace-nowrap text-right`}>
                                                      <div style={{ display: "inline-flex", gap: "8px", justifyContent: "flex-end", flexWrap: "wrap" }}>
                                                        <button
                                                          type="button"
                                                          onClick={() => startRevenueEdit(rr)}
                                                          disabled={tenancyRevenueLoadingId === String(tn.id)}
                                                          className="rounded-[8px] border border-black/10 bg-transparent px-2.5 py-1 text-xs font-bold text-[#64748b] hover:bg-slate-100 dark:border-white/[0.1] dark:text-[#8090b0] dark:hover:bg-white/[0.05] disabled:cursor-default"
                                                          style={{ cursor: tenancyRevenueLoadingId === String(tn.id) ? "default" : "pointer" }}
                                                        >
                                                          Bearbeiten
                                                        </button>
                                                        <button
                                                          type="button"
                                                          onClick={() => deleteRevenueRow(tn.id, rr, tn)}
                                                          disabled={tenancyRevenueLoadingId === String(tn.id)}
                                                          className="rounded-[8px] border border-red-500/20 bg-red-500/10 px-2.5 py-1 text-xs font-bold text-[#f87171] hover:bg-red-500/15 disabled:cursor-default"
                                                          style={{ cursor: tenancyRevenueLoadingId === String(tn.id) ? "default" : "pointer" }}
                                                        >
                                                          Löschen
                                                        </button>
                                                      </div>
                                                    </td>
                                                  </tr>
                                                );
                                              })
                                            ) : (
                                              <tr>
                                                <td colSpan={6} className={`${tdCellClass} text-[#64748b] dark:text-[#6b7a9a]`}>
                                                  {tenancyRevenueLoadingId === String(tn.id) ? "Lade …" : "Keine Einnahmen erfasst."}
                                                </td>
                                              </tr>
                                            )}
                                          </tbody>
                                        </table>
                                      </div>

                                      <p className="mt-2.5 text-[11px] text-[#64748b] dark:text-[#6b7a9a]">
                                        Berechnung Gesamteinnahmen / Monat: monatlich voll, jährlich ÷12; einmalige Beträge unter „Einmalige Einnahmen“.
                                      </p>
                                    </div>
                                </div>
                              ) : null}
                              <TenancyCancelDialog
                                tenancy={cancelTarget}
                                onClose={() => setCancelTarget(null)}
                                onCancelled={handleCancelled}
                              />
                              <AlertDialog
                                open={moveInConfirmOpen}
                                onOpenChange={setMoveInConfirmOpen}
                              >
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Mietbeginn wirklich ändern?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Der Mietbeginn dieses Mietverhältnisses wird geändert.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <div className="text-[13px] text-[#0f172a] dark:text-[#eef2ff]">
                                    <div className="mb-2">
                                      Bisher:{" "}
                                      <strong>{formatDateOnly(moveInOldNorm || "") || "—"}</strong>
                                      {" → "}Neu:{" "}
                                      <strong>{formatDateOnly(moveInNewNorm || "") || "—"}</strong>
                                    </div>
                                    <div className="mb-1 font-semibold">
                                      Folgende Daten werden NICHT automatisch geändert:
                                    </div>
                                    <ul className="list-disc pl-5">
                                      <li>einmalige Gebühren und deren Buchungsdatum</li>
                                      <li>Co-Mieter-Nachträge und deren Wirksamkeitsdatum</li>
                                      <li>bereits generierte Mietverträge oder andere PDFs</li>
                                      <li>bereits erstellte oder versendete Rechnungen</li>
                                      <li>Mietkautionsversicherungen und andere externe Dokumente</li>
                                    </ul>
                                    <div className="mt-2 text-[#64748b] dark:text-[#8090b0]">
                                      Es wird keine Bexio-Aktion ausgeführt.
                                    </div>
                                  </div>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel disabled={tenancyEditSaving}>
                                      Abbrechen
                                    </AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={handleMoveInConfirm}
                                      disabled={tenancyEditSaving}
                                    >
                                      Mietbeginn ändern
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </React.Fragment>
  );
}
